"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { MapPin, Phone, Mail, Clock, Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ContactBooking() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="contact-booking" ref={sectionRef} className={`py-24 px-4 relative overflow-hidden bg-accent transition-all duration-500 ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549040/img/palm_fronds_texture.png)] bg-cover bg-center opacity-10 pointer-events-none" aria-hidden="true" />
      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16">
          {}
          <div className="space-y-8">
            <div>
              <h2 className="font-display text-4xl lg:text-5xl mb-4 text-foreground">
                Get in Touch
              </h2>
              <p className="text-black text-lg">
                Ready to embark on an unforgettable eco-luxury adventure? Reach out to us and let's start planning your dream experience in the Riviera Maya.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4 group transition-all duration-100" style={{
              transform: "translateX(0)"
            }} onMouseEnter={e => {
              e.currentTarget.style.transform = "translateX(5px)";
            }} onMouseLeave={e => {
              e.currentTarget.style.transform = "translateX(0)";
            }}>
                <div className="flex-shrink-0 size-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <MapPin className="size-5 text-primary-dark" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Location</h3>
                  <p className="text-foreground">Playa del Carmen, Riviera Maya, Mexico</p>
                </div>
              </div>

              <div className="flex items-start gap-4 group transition-all duration-100" style={{
              transform: "translateX(0)"
            }} onMouseEnter={e => {
              e.currentTarget.style.transform = "translateX(5px)";
            }} onMouseLeave={e => {
              e.currentTarget.style.transform = "translateX(0)";
            }}>
                <div className="flex-shrink-0 size-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Phone className="size-5 text-primary-dark" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Phone</h3>
                  <p className="text-foreground">+52 984 123 4567</p>
                </div>
              </div>

              <div className="flex items-start gap-4 group transition-all duration-100" style={{
              transform: "translateX(0)"
            }} onMouseEnter={e => {
              e.currentTarget.style.transform = "translateX(5px)";
            }} onMouseLeave={e => {
              e.currentTarget.style.transform = "translateX(0)";
            }}>
                <div className="flex-shrink-0 size-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Mail className="size-5 text-primary-dark" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Email</h3>
                  <p className="text-foreground">contact@ecoluxmaya.com</p>
                </div>
              </div>

              <div className="flex items-start gap-4 group transition-all duration-100" style={{
              transform: "translateX(0)"
            }} onMouseEnter={e => {
              e.currentTarget.style.transform = "translateX(5px)";
            }} onMouseLeave={e => {
              e.currentTarget.style.transform = "translateX(0)";
            }}>
                <div className="flex-shrink-0 size-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Clock className="size-5 text-primary-dark" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Hours</h3>
                  <p className="text-foreground">Monday to Sunday: 8:00 AM – 8:00 PM</p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="card p-8 lg:p-10">
            <FormProvider formId="contact_booking_form_home" sectionName="contact-booking" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            travel_dates: "",
            guests: "",
            experience_type: "",
            message: ""
          }} validationRules={{
            full_name: {
              required: true,
              minLength: 2,
              maxLength: 100
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            travel_dates: {
              required: true
            },
            guests: {
              required: true,
              min: 1,
              max: 20
            },
            experience_type: {
              required: true
            },
            message: {
              required: false
            }
          }} submitText="Request Booking" successMessage="Thank you for your booking request! We'll contact you shortly to confirm your eco-luxury experience.">
              <ContactBookingForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function ContactBookingForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-primary-light border border-primary/20 rounded-lg">
        <p className="text-primary-dark text-lg mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="w-full">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-5">
        <FormField control={control} name="full_name" rules={{
        required: "Full name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        },
        maxLength: {
          value: 100,
          message: "Name must be less than 100 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" {...field} className="transition-all duration-100 focus-ring" />
              </FormControl>
              <FormDescription>Please enter your first and last name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" {...field} className="transition-all duration-100 focus-ring" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[\d\s\-\+\(\)]+$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 555-123-4567" {...field} className="transition-all duration-100 focus-ring" />
              </FormControl>
              <FormDescription>International format preferred.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="travel_dates" rules={{
        required: "Travel dates are required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Preferred Travel Dates</FormLabel>
              <FormControl>
                <Input placeholder="MM/DD/YYYY – MM/DD/YYYY" {...field} className="transition-all duration-100 focus-ring" />
              </FormControl>
              <FormDescription>Enter the start and end dates of your trip.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="guests" rules={{
        required: "Number of guests is required",
        min: {
          value: 1,
          message: "At least 1 guest is required"
        },
        max: {
          value: 20,
          message: "Maximum 20 guests allowed"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Number of Guests</FormLabel>
              <FormControl>
                <Input type="number" placeholder="2" min={1} max={20} {...field} className="transition-all duration-100 focus-ring" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="experience_type" rules={{
        required: "Please select an experience type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Preferred Experience</FormLabel>
              <FormControl>
                <select {...field} className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-all outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm">
                  <option value="">Select an option</option>
                  <option value="Adventure">Adventure</option>
                  <option value="Cultural">Cultural</option>
                  <option value="Relaxation">Relaxation</option>
                  <option value="Custom Luxury">Custom Luxury</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" render={({
        field
      }) => <FormItem>
              <FormLabel>Additional Details / Questions</FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us more about your interests, special requests, or any questions you have." {...field} className="min-h-32 transition-all duration-100 focus-ring" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-secondary-light border border-secondary/30 rounded-lg">
            <p className="text-secondary-dark">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-semibold uppercase tracking-wider transition-all duration-150 hover:shadow-medium relative overflow-hidden group">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Sending Request...
            </> : <>
              <span className="relative z-10">Request Booking</span>
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" aria-hidden="true" />
            </>}
        </Button>
      </div>
    </Form>;
}